import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { ShoppingBag, MagnifyingGlass, User, List, X, SignOut, Gauge } from "@phosphor-icons/react";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FAFAF8]/95 backdrop-blur-md border-b border-stone-200" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 gap-6">
          <Link to="/" className="shrink-0" data-testid="logo-link">
            <div className="font-serif text-2xl md:text-3xl tracking-tight text-stone-900 leading-none">Luxe Aroma Scents</div>
            <div className="label-caps text-[#C0A062] text-[0.6rem]">luxearomascents.com</div>
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl items-center bg-white border border-stone-200 rounded-full px-4 py-2.5" data-testid="search-form">
            <MagnifyingGlass size={18} className="text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar fragancias, velas, decoración..."
              className="flex-1 bg-transparent outline-none px-3 text-sm text-stone-800"
              data-testid="search-input"
            />
          </form>

          <div className="flex items-center gap-5">
            {user && user.role === "admin" && (
              <Link to="/admin" className="hidden sm:flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900" data-testid="admin-link">
                <Gauge size={20} /> <span className="hidden lg:inline">Admin</span>
              </Link>
            )}
            {user ? (
              <div className="hidden sm:flex items-center gap-4">
                <Link to="/orders" className="flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900" data-testid="orders-link">
                  <User size={20} /> <span className="hidden lg:inline">{user.name?.split(" ")[0]}</span>
                </Link>
                <button onClick={logout} className="text-stone-500 hover:text-stone-900" data-testid="logout-button" title="Cerrar sesión">
                  <SignOut size={20} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="hidden sm:flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900" data-testid="login-link">
                <User size={20} /> <span className="hidden lg:inline">Cuenta</span>
              </Link>
            )}
            <Link to="/cart" className="relative" data-testid="cart-link">
              <ShoppingBag size={24} className="text-stone-800" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C0A062] text-white text-[0.65rem] w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">{count}</span>
              )}
            </Link>
            <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
              {menuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {/* Departments nav */}
        <nav className="hidden lg:flex items-center gap-8 h-12 border-t border-stone-100" data-testid="departments-nav">
          <Link to="/shop" className="label-caps text-stone-600 hover:text-stone-900 transition-colors" data-testid="nav-all">Todo</Link>
          <Link to="/shop?on_sale=true" className="label-caps text-[#991B1B] font-semibold hover:text-[#7f1616] transition-colors" data-testid="nav-venta">Venta</Link>
          {departments.map((d) => (
            <Link key={d} to={`/shop?department=${encodeURIComponent(d)}`} className="label-caps text-stone-600 hover:text-stone-900 transition-colors" data-testid={`nav-dept-${d}`}>{d}</Link>
          ))}
        </nav>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-stone-200 px-6 py-4 space-y-3" data-testid="mobile-menu">
          <form onSubmit={submitSearch} className="flex items-center bg-stone-100 rounded-full px-4 py-2.5">
            <MagnifyingGlass size={18} className="text-stone-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="flex-1 bg-transparent outline-none px-3 text-sm" data-testid="mobile-search-input" />
          </form>
          <Link to="/shop" onClick={() => setMenuOpen(false)} className="block py-2 label-caps text-stone-700">Todo</Link>
          <Link to="/shop?on_sale=true" onClick={() => setMenuOpen(false)} className="block py-2 label-caps text-[#991B1B] font-semibold">Venta</Link>
          {departments.map((d) => (
            <Link key={d} to={`/shop?department=${encodeURIComponent(d)}`} onClick={() => setMenuOpen(false)} className="block py-2 label-caps text-stone-700">{d}</Link>
          ))}
          <div className="border-t border-stone-200 pt-3">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setMenuOpen(false)} className="block py-2 text-sm">Panel Admin</Link>}
                <Link to="/orders" onClick={() => setMenuOpen(false)} className="block py-2 text-sm">Mis Pedidos</Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="block py-2 text-sm text-stone-600">Cerrar sesión</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-2 text-sm">Iniciar sesión</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
